import React, { useState } from 'react';
import { Button, styled, TextField, Typography } from '@mui/material';
import InsideLoading from 'components/loading/InsideLoading';
import { usePermissionRules } from 'hooks/permissionRules';
import { moneyFormat } from 'helpers/moneyFormat';
import { BudgetPayment } from 'types/budgetPayment';
import BudgetPaymentForm from './BudgetPaymentForm';
import { useBudgetPayments } from './useBudgetPayments';

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

const Totals = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const Total = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.25, 1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.grey[50],
}));

const Row = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '90px minmax(0, 1fr) 110px auto',
  columnGap: theme.spacing(1.5),
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr auto',
  },
}));

function formatDay(value: string) {
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
}

interface BudgetPaymentsPanelProps {
  total: number;
  paid: number;
  payments: ReturnType<typeof useBudgetPayments>;
}

const BudgetPaymentsPanel: React.FC<BudgetPaymentsPanelProps> = ({ total, paid, payments }) => {
  const { checkPermission } = usePermissionRules();
  const { data, methods, loading, saving, addPayment, reversePayment } = payments;
  const [reversing, setReversing] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const canInform = checkPermission('surgeries.closed.confirm-payment');
  const canReverse = checkPermission('surgeries.payments.reverse');
  const balance = Math.max(Math.round((total - paid) * 100) / 100, 0);
  // estornos ficam só no histórico
  const active = (data?.payments || []).filter(payment => payment.status === 'active');
  // orçamentos antigos têm o total pago, mas não os pagamentos detalhados
  const legacyPaid = Math.max(
    Math.round((paid - active.reduce((sum, payment) => sum + payment.value, 0)) * 100) / 100,
    0,
  );

  const canConfirm = !saving && reason.trim().length >= 3;

  function cancelReversal() {
    setReversing(null);
    setReason('');
  }

  function handleReverse(payment: BudgetPayment) {
    reversePayment(payment.id, reason.trim()).then(ok => {
      if (!ok) return;

      setReversing(null);
      setReason('');
    });
  }

  return (
    <Container>
      <Totals>
        <Total>
          <Typography variant="caption" color="textSecondary">
            Total do orçamento
          </Typography>
          <Typography variant="h6">{moneyFormat(total)}</Typography>
        </Total>
        <Total>
          <Typography variant="caption" color="textSecondary">
            Pago
          </Typography>
          <Typography variant="h6" color="success.main">
            {moneyFormat(paid)}
          </Typography>
        </Total>
        <Total>
          <Typography variant="caption" color="textSecondary">
            Saldo
          </Typography>
          <Typography variant="h6" color={balance > 0 ? 'error' : 'textPrimary'}>
            {moneyFormat(balance)}
          </Typography>
        </Total>
      </Totals>

      {loading ? (
        <InsideLoading />
      ) : (
        <>
          {canInform && balance > 0 && (
            <section>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Informar pagamento
              </Typography>
              <BudgetPaymentForm balance={balance} methods={methods} saving={saving} onSubmit={addPayment} />
            </section>
          )}

          <section>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Pagamentos
            </Typography>

            {legacyPaid > 0 && (
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                {moneyFormat(legacyPaid)} foram pagos antes do registro detalhado de pagamentos.
              </Typography>
            )}

            {active.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Nenhum pagamento informado.
              </Typography>
            ) : (
              active.map(payment => (
                <div key={payment.id}>
                  <Row>
                    <Typography variant="body2">{formatDay(payment.paid_at)}</Typography>
                    <div style={{ minWidth: 0 }}>
                      <Typography variant="body2">{payment.payment_method?.name || '-'}</Typography>
                      <Typography variant="caption" color="textSecondary" noWrap display="block">
                        Informado por {payment.created_by}
                      </Typography>
                    </div>
                    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right' }}>
                      {moneyFormat(payment.value)}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      disabled={!canReverse || saving}
                      onClick={() => setReversing(reversing === payment.id ? null : payment.id)}
                    >
                      Estornar
                    </Button>
                  </Row>

                  {reversing === payment.id && (
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '4px 0 12px' }}
                    >
                      <TextField
                        autoFocus
                        size="small"
                        label="Motivo do estorno"
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Escape') cancelReversal();
                          if (e.key === 'Enter' && canConfirm) handleReverse(payment);
                        }}
                        sx={{ flex: '1 1 240px' }}
                      />
                      <Button color="inherit" disabled={saving} onClick={cancelReversal} sx={{ whiteSpace: 'nowrap' }}>
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        disabled={!canConfirm}
                        onClick={() => handleReverse(payment)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {saving ? 'Estornando...' : 'Confirmar estorno'}
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </section>
        </>
      )}
    </Container>
  );
};

export default BudgetPaymentsPanel;
